import { useMemo } from "react";
import { useIntl } from "react-intl";

import { formatISODateToMMDDYYYY, isDateWithinDays } from "~/utils/common";

import BalanceProgressBar from "./balance-progress-bar";
import ExpiryWarning from "./expiry-warning";
import LeftoverAmount from "./leftover-amount";
import PlanBadge from "./plan-badge";
import { getPlanType } from "./plan-type";
import type { TpaTrackerProps } from "./types";
import { formatCurrency } from "./utils";

const TpaTracker: React.FC<TpaTrackerProps> = ({
  trackerData,
  activeCurrency,
  balanceTrackerExpiryDays,
  popupData,
  onDetailsClick,
  hideExpiryMessages = false,
  "data-testid": testId,
}) => {
  const intl = useIntl();

  const plansWithUpcomingExpiry = useMemo(() => {
    if (!balanceTrackerExpiryDays || !popupData) return [];
    return popupData
      .filter(plan => {
        if (!plan.expiryDate) return false;
        const formattedDate = formatISODateToMMDDYYYY(plan.expiryDate);
        return (
          formattedDate &&
          isDateWithinDays(formattedDate, balanceTrackerExpiryDays)
        );
      })
      .map(plan => ({
        planType: getPlanType(plan.accountName),
        balance: formatCurrency(plan.balance, intl, activeCurrency),
        deadlineDate:
          formatISODateToMMDDYYYY(plan.expiryDate ?? undefined) ?? "",
      }));
  }, [balanceTrackerExpiryDays, popupData, intl, activeCurrency]);

  // `hideExpiryMessages` (account-popover placement) suppresses the deadline
  // warnings under the pills and the progress-bar asterisk that points at them.
  const expiryPlans = hideExpiryMessages ? [] : plansWithUpcomingExpiry;
  const hasUpcomingExpiry = expiryPlans.length > 0;

  const currentAvailableBalance =
    parseFloat(trackerData?.currentAvailableBalance) || 0;
  const availableAmount = formatCurrency(
    currentAvailableBalance,
    intl,
    activeCurrency,
  );

  const balanceUsagePercentageNumber =
    parseFloat(trackerData?.balanceUsagePercentage) || 0;

  const usedSessionBalance = parseFloat(trackerData?.usedSessionBalance) || 0;
  const leftToSpend = Math.max(0, currentAvailableBalance - usedSessionBalance);
  const leftToSpendAmount = formatCurrency(leftToSpend, intl, activeCurrency);

  const leftoverOrderAmountInt =
    parseFloat(trackerData?.leftoverOrderAmount ?? "0") || 0;
  const leftoverOrderAmount =
    leftoverOrderAmountInt > 0
      ? formatCurrency(leftoverOrderAmountInt, intl, activeCurrency)
      : null;

  const isLeftoverAmountValid =
    !isNaN(leftoverOrderAmountInt) && leftoverOrderAmountInt > 0;

  const isMultiPlan = popupData && popupData.length > 1;

  return (
    <div
      className="relative w-full bg-transparent py-2 text-emphasis"
      data-testid={testId}
    >
      <div className="mb-1 flex items-center justify-between">
        {/* Plain text with no handler — a focusable button that does nothing
            is a false affordance (account-card placement, COM-17). */}
        {onDetailsClick ? (
          <button
            type="button"
            className="text-lg font-medium text-account-page-card-body-content-color hover:cursor-pointer"
            onClick={onDetailsClick}
          >
            Balance Display™
          </button>
        ) : (
          <span className="text-lg font-medium text-account-page-card-body-content-color">
            Balance Display™
          </span>
        )}
        {onDetailsClick && (
          <button
            onClick={onDetailsClick}
            className="whitespace-nowrap text-xs font-bold text-teal-01 underline hover:no-underline"
            data-testid="tracker-DetailsLink"
          >
            Details
          </button>
        )}
      </div>

      <BalanceProgressBar
        percentage={balanceUsagePercentageNumber}
        leftToSpend={leftToSpendAmount}
        available={availableAmount}
        showAsterisk={!!(isMultiPlan && hasUpcomingExpiry)}
        showAvailableToSpend={true}
        onClick={onDetailsClick}
        bgColor="bg-account-page-card-body-content-color"
        largeFonts={true}
      />

      {isMultiPlan && popupData && (
        <div className="flex w-full flex-col gap-2">
          <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="badge_container flex w-full flex-row flex-wrap items-center gap-2 md:w-auto">
              {popupData.map(plan => (
                <PlanBadge
                  key={`${plan.accountName}-${plan.expiryDate ?? "no-expiry"}`}
                  accountName={plan.accountName}
                  balance={plan.balance}
                  currency={activeCurrency}
                />
              ))}
              <div className="hidden md:flex md:flex-row md:gap-2">
                {expiryPlans.map(plan => (
                  <ExpiryWarning
                    key={`${plan.planType}-${plan.deadlineDate}`}
                    planType={plan.planType}
                    balance={plan.balance}
                    deadlineDate={plan.deadlineDate}
                    showPlanType={!!isMultiPlan}
                  />
                ))}
              </div>
            </div>
            <div className="amount_container hidden md:block">
              {isLeftoverAmountValid && leftoverOrderAmount && (
                <LeftoverAmount amount={leftoverOrderAmount} />
              )}
            </div>
          </div>
          {expiryPlans.length > 0 && (
            <div className="flex w-full flex-row gap-2 md:hidden">
              {expiryPlans.map(plan => (
                <ExpiryWarning
                  key={`${plan.planType}-${plan.deadlineDate}-mobile`}
                  planType={plan.planType}
                  balance={plan.balance}
                  deadlineDate={plan.deadlineDate}
                  showPlanType={!!isMultiPlan}
                />
              ))}
            </div>
          )}
          {isLeftoverAmountValid && leftoverOrderAmount && (
            <div className="amount_container w-full md:hidden">
              <LeftoverAmount amount={leftoverOrderAmount} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TpaTracker;
